import { motion } from "framer-motion";
import { 
  Eye, Headphones, Brain, Mic, Phone, Heart,
  ShieldCheck, TrendingUp, Clock, MessageSquare,
  AlertTriangle, Users, Camera, Sparkles
} from "lucide-react";

const supervisors = [
  {
    id: "panoptes",
    greekName: "APOLLO",
    title: "Supervisor Apollo",
    subtitle: "The Light-Bringer",
    role: "Vision AI",
    icon: Eye,
    color: "from-lime to-green-500",
    bgGlow: "lime",
    features: [
      "Food spoilage and quality detection",
      "Plating consistency checks",
      "Food safety observations",
      "Actionable operational findings",
      "Privacy-first: frame analyzed, not stored",
      "Upload-based in V1 — continuous camera coming soon"
    ],
    stat: { value: "V1", label: "image analysis available" },
    personality: "Vigilant guardian who never sleeps"
  },
  {
    id: "hermes",
    greekName: "ECHO",
    title: "Supervisor Echo",
    subtitle: "The Voice",
    role: "AI Phone Receptionist",
    icon: Headphones,
    color: "from-gold to-amber-500",
    bgGlow: "gold",
    features: [
      "24/7 AI phone reception",
      "Menu and allergy query handling",
      "Intelligent call routing",
      "Natural conversational responses",
      "Website voice experience",
      "Talk to Echo: call or use web widget"
    ],
    stat: { value: "24/7", label: "phone coverage" },
    badge: "VAPI POWERED",
    personality: "Charming diplomat with perfect recall"
  },
  {
    id: "athena",
    greekName: "ATHENA",
    title: "Supervisor Athena",
    subtitle: "The Strategist",
    role: "SOP Builder",
    icon: Brain,
    color: "from-purple-400 to-indigo-500",
    bgGlow: "purple",
    features: [
      "Voice-to-SOP creation",
      "Ingredient cost analysis",
      "Step-by-step procedure documentation",
      "Team-ready formatting",
      "Operational knowledge capture",
      "AI-guided improvement suggestions"
    ],
    stat: { value: "V1", label: "SOP builder available" },
    personality: "Wise counsel who ensures excellence"
  },
  {
    id: "hephaestus",
    greekName: "ATLAS",
    title: "Supervisor Atlas",
    subtitle: "The Carrier",
    role: "Staff Coordination",
    icon: Users,
    color: "from-blue-400 to-cyan-500",
    bgGlow: "blue",
    features: [
      "Staff task routing intelligence",
      "Operational broadcast alerts",
      "Multi-supervisor coordination",
      "Real-time team communication",
      "BYOD and wearable support — coming Q3 2026",
      "Staff mobile app — coming Q3 2026"
    ],
    stat: { value: "Q3", label: "full feature suite" },
    badge: "COMING SOON",
    personality: "Master coordinator of your team"
  },
  {
    id: "iris",
    greekName: "IRIS",
    title: "Supervisor Iris",
    subtitle: "The Messenger",
    role: "Kitchen Traffic Control",
    icon: MessageSquare,
    color: "from-pink-400 to-rose-500",
    bgGlow: "pink",
    features: [
      "Kitchen task routing",
      "Order-ready → right runner routing",
      "Guest issue escalation to manager",
      "Operational workload awareness",
      "KDS integration — coming Q3 2026",
      "Real-time kitchen flow analytics — coming soon"
    ],
    stat: { value: "V1", label: "routing layer active" },
    badge: "NEW",
    personality: "Rainbow messenger connecting all supervisors"
  }
];

const SupervisorCard = ({ supervisor, index }) => {
  const Icon = supervisor.icon;
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.1 }}
      className="relative group"
    >
      {/* Glow effect */}
      <div className={`absolute -inset-0.5 bg-gradient-to-r ${supervisor.color} rounded-xl blur opacity-20 group-hover:opacity-40 transition duration-500`} />
      
      <div className="relative glass-card rounded-xl p-6 border border-white/10 h-full">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${supervisor.color} flex items-center justify-center shadow-lg`}>
            <Icon className="w-7 h-7 text-white" />
          </div>
          {supervisor.badge && (
            <span className="px-2 py-1 bg-green-500/20 text-green-400 text-[10px] font-bold rounded uppercase tracking-wider">
              {supervisor.badge}
            </span>
          )}
        </div>
        
        {/* Greek Name Badge */}
        <div className="mb-2">
          <span className={`text-[10px] font-bold tracking-[0.3em] bg-gradient-to-r ${supervisor.color} bg-clip-text text-transparent`}>
            {supervisor.greekName}
          </span>
        </div>
        
        {/* Title */}
        <div className="mb-1">
          <h3 className="font-inter text-xl font-black text-white">
            {supervisor.title}
          </h3>
          <p className="text-xs text-slate-400 italic">{supervisor.subtitle}</p>
        </div>
        
        {/* Role Badge */}
        <div className="mb-4">
          <span className="px-2 py-1 bg-white/5 border border-white/10 rounded text-xs text-slate-300">
            {supervisor.role}
          </span>
        </div>
        
        {/* Features */}
        <ul className="space-y-2 mb-6">
          {supervisor.features.map((f, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-slate-300">
              <Sparkles className="w-3 h-3 text-gold mt-1 shrink-0" />
              <span>{f}</span>
            </li>
          ))}
        </ul>
        
        {/* Stat */}
        <div className="pt-4 border-t border-white/10">
          <div className="flex items-baseline gap-2">
            <span className={`text-3xl font-black bg-gradient-to-r ${supervisor.color} bg-clip-text text-transparent`}>
              {supervisor.stat.value}
            </span>
            <span className="text-xs text-slate-400">{supervisor.stat.label}</span>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export const FeaturesShowcase = () => {
  return (
    <section data-testid="features-showcase" className="py-24 md:py-32 relative overflow-hidden">
      {/* Background elements */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-lime/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-gold/10 rounded-full blur-3xl" />
      </div>
      
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <span className="font-inter text-xs font-bold tracking-[0.3em] uppercase text-lime">
            Meet Your AI Leadership Team
          </span>
          <h2 className="font-inter text-4xl md:text-5xl lg:text-6xl font-black text-white mt-4">
            Five <span className="text-gold">Supervisors.</span> One Vision.
          </h2>
          <p className="text-base md:text-lg text-slate-400 mt-4 max-w-2xl mx-auto">
            Each supervisor is an expert in their domain, but they work as one unified intelligence.
            When Apollo sees a problem, Echo can explain it, Athena documents it, Atlas alerts your team, and Iris routes it to the right person.
          </p>
        </motion.div>
        
        {/* Architecture diagram */}
        <motion.div 
          className="glass-card rounded-xl border border-gold/20 p-6 mb-16 max-w-5xl mx-auto"
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
        >
          <div className="text-center mb-4">
            <span className="text-xs font-bold text-gold tracking-wider uppercase">THE ARGUS COUNCIL</span>
          </div>
          <div className="grid grid-cols-5 gap-2 md:gap-4 text-center">
            <div className="p-2 md:p-3 rounded-lg bg-lime/10 border border-lime/20">
              <Eye className="w-5 h-5 md:w-6 md:h-6 text-lime mx-auto mb-1 md:mb-2" />
              <span className="text-[8px] md:text-[10px] text-lime font-bold block">APOLLO</span>
              <span className="text-[7px] md:text-[9px] text-slate-500 hidden sm:block">The Eyes</span>
            </div>
            <div className="p-2 md:p-3 rounded-lg bg-gold/10 border border-gold/20">
              <Headphones className="w-5 h-5 md:w-6 md:h-6 text-gold mx-auto mb-1 md:mb-2" />
              <span className="text-[8px] md:text-[10px] text-gold font-bold block">ECHO</span>
              <span className="text-[7px] md:text-[9px] text-slate-500 hidden sm:block">The Ears</span>
            </div>
            <div className="p-2 md:p-3 rounded-lg bg-purple-400/10 border border-purple-400/20">
              <Brain className="w-5 h-5 md:w-6 md:h-6 text-purple-400 mx-auto mb-1 md:mb-2" />
              <span className="text-[8px] md:text-[10px] text-purple-400 font-bold block">ATHENA</span>
              <span className="text-[7px] md:text-[9px] text-slate-500 hidden sm:block">The Brain</span>
            </div>
            <div className="p-2 md:p-3 rounded-lg bg-blue-400/10 border border-blue-400/20">
              <Users className="w-5 h-5 md:w-6 md:h-6 text-blue-400 mx-auto mb-1 md:mb-2" />
              <span className="text-[8px] md:text-[10px] text-blue-400 font-bold block">ATLAS</span>
              <span className="text-[7px] md:text-[9px] text-slate-500 hidden sm:block">The Hands</span>
            </div>
            <div className="p-2 md:p-3 rounded-lg bg-pink-400/10 border border-pink-400/20 relative">
              <span className="absolute -top-1 -right-1 text-[6px] bg-pink-400 text-white px-1 rounded font-bold">NEW</span>
              <MessageSquare className="w-5 h-5 md:w-6 md:h-6 text-pink-400 mx-auto mb-1 md:mb-2" />
              <span className="text-[8px] md:text-[10px] text-pink-400 font-bold block">IRIS</span>
              <span className="text-[7px] md:text-[9px] text-slate-500 hidden sm:block">The Messenger</span>
            </div>
          </div>
          <div className="flex justify-center mt-4">
            <div className="flex items-center gap-2 text-xs text-slate-400">
              <div className="w-8 h-px bg-gradient-to-r from-lime via-gold to-pink-400" />
              <span>Unified Intelligence Network</span>
              <div className="w-8 h-px bg-gradient-to-r from-pink-400 via-gold to-lime" />
            </div>
          </div>
        </motion.div>
        
        {/* Supervisor Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
          {supervisors.map((supervisor, index) => (
            <SupervisorCard key={supervisor.id} supervisor={supervisor} index={index} />
          ))}
        </div>
        
        {/* Bottom CTA */}
        <motion.div 
          className="mt-16 text-center"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <p className="text-slate-400 mb-4">
            <span className="text-gold font-bold">All five supervisors included</span> in your Founding Member subscription.
          </p>
          <p className="text-sm text-slate-500">
            No add-ons. No per-supervisor pricing. One flat rate forever.
          </p>
        </motion.div>
      </div>
    </section>
  );
};
